import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../../lib/utils';

/**
 * Status is encoded in colour *and* shape-of-word, never colour alone — the
 * label always says what the state is.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ' +
    'font-mono text-[0.6875rem] tracking-wide uppercase',
  {
    variants: {
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        active: 'bg-primary-muted text-primary',
        success: 'bg-success-muted text-success',
        warning: 'bg-warning-muted text-warning',
        danger: 'bg-danger-muted text-danger',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps extends ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  /** Show a leading dot — useful for live/processing states. */
  dot?: boolean | undefined;
  pulse?: boolean | undefined;
}

export function Badge({ className, tone, dot, pulse, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {dot && (
        <span
          aria-hidden
          className={cn('size-1.5 rounded-full bg-current', pulse && 'animate-pulse')}
        />
      )}
      {children}
    </span>
  );
}
